import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { QueryResult, ValidateQueryResponse } from '@idas/shared';
import { DEFAULT_ROW_LIMIT } from '@idas/shared';

export interface QueryTab {
  id: string;
  title: string;
  sql: string;
  dataSourceId: string | null;
  /**
   * The schema unqualified table names resolve against.
   *
   * Per tab rather than global, for the same reason the connection is: two
   * tabs may legitimately sit on different databases, and silently retargeting
   * one because the other changed would rewrite what a query means.
   */
  database: string | null;
  /** Populated by the debounced validate call as the analyst types. */
  validation: ValidateQueryResponse | null;
  result: QueryResult | null;
  running: boolean;
  /** The server's id for the completed run. Only known once it finishes. */
  runId: string | null;
  /**
   * The id sent with the request, set before it leaves the browser.
   *
   * This is what Cancel uses: `runId` does not exist until the response
   * arrives, which is exactly when cancelling stops being useful.
   */
  clientQueryId: string | null;
  error: string | null;
}

interface ConsoleState {
  tabs: QueryTab[];
  activeTabId: string;
  dataSourceId: string | null;
  theme: 'dark' | 'light';
  /** Rows the analyst asked for. Clamped down by the server, never up. */
  rowLimit: number;

  setDataSource: (id: string) => void;
  /** Sets the active database on the current tab. */
  setDatabase: (database: string | null) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  setRowLimit: (rows: number) => void;
  addTab: (sql?: string) => string;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTab: (id: string, patch: Partial<QueryTab>) => void;
  renameTab: (id: string, title: string) => void;
}

function makeTab(
  index: number,
  dataSourceId: string | null,
  sql = '',
  database: string | null = null,
): QueryTab {
  return {
    id: crypto.randomUUID(),
    title: `Query ${index}`,
    sql,
    dataSourceId,
    database,
    validation: null,
    result: null,
    running: false,
    runId: null,
    clientQueryId: null,
    error: null,
  };
}

export const useConsoleStore = create<ConsoleState>()(
  persist(
    (set, get) => {
      const initial = makeTab(1, null);

      return {
        tabs: [initial],
        activeTabId: initial.id,
        dataSourceId: null,
        theme: 'light',
        rowLimit: DEFAULT_ROW_LIMIT,

        setDataSource: (id) =>
          set((state) => ({
            dataSourceId: id,
            // Point tabs that have not chosen a source at the new one, but
            // leave a tab that deliberately targets another connection alone.
            tabs: state.tabs.map((tab) =>
              tab.dataSourceId === null || tab.dataSourceId === state.dataSourceId
                ? // The database belongs to the old connection's schema list,
                  // so it is cleared rather than carried across. The console
                  // re-selects one as soon as the new catalog arrives.
                  { ...tab, dataSourceId: id, database: null, validation: null }
                : tab,
            ),
          })),

        setDatabase: (database) =>
          set((state) => ({
            tabs: state.tabs.map((tab) =>
              tab.id === state.activeTabId
                ? // Validation is dropped because the same SQL can be valid
                  // against one database and refused against another.
                  { ...tab, database, validation: null }
                : tab,
            ),
          })),

        setTheme: (theme) => set({ theme }),

        setRowLimit: (rowLimit) => set({ rowLimit }),

        addTab: (sql) => {
          const current = get().tabs.find((t) => t.id === get().activeTabId);
          const tab = makeTab(
            get().tabs.length + 1,
            get().dataSourceId,
            sql,
            current?.database ?? null,
          );
          set((state) => ({ tabs: [...state.tabs, tab], activeTabId: tab.id }));
          return tab.id;
        },

        closeTab: (id) =>
          set((state) => {
            // Never leave the console with no editor at all.
            if (state.tabs.length === 1) return state;

            const index = state.tabs.findIndex((t) => t.id === id);
            const tabs = state.tabs.filter((t) => t.id !== id);
            const activeTabId =
              state.activeTabId === id
                ? // Focus the neighbour, which is where the eye already is.
                  (tabs[Math.max(0, index - 1)]?.id ?? tabs[0]!.id)
                : state.activeTabId;

            return { tabs, activeTabId };
          }),

        setActiveTab: (id) => set({ activeTabId: id }),

        updateTab: (id, patch) =>
          set((state) => ({
            tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, ...patch } : tab)),
          })),

        renameTab: (id, title) =>
          set((state) => ({
            tabs: state.tabs.map((tab) => (tab.id === id ? { ...tab, title } : tab)),
          })),
      };
    },
    {
      name: 'idas-console',
      /**
       * SQL text and tab layout survive a reload; results never do.
       *
       * Persisting result rows would write restricted data into localStorage,
       * where it outlives the session, survives sign-out, and is readable by
       * anything running on the origin. The whole point of this system is that
       * data does not accumulate outside it.
       */
      partialize: (state) => ({
        theme: state.theme,
        rowLimit: state.rowLimit,
        dataSourceId: state.dataSourceId,
        activeTabId: state.activeTabId,
        tabs: state.tabs.map((tab) => ({
          ...tab,
          result: null,
          validation: null,
          running: false,
          runId: null,
    clientQueryId: null,
          error: null,
        })),
      }),
    },
  ),
);
